import React, { useCallback, useState } from 'react';
import { useFocusEffect } from '@react-navigation/native';
import { ActivityIndicator, Pressable, ScrollView, StyleSheet, View } from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import { Text } from '../components/Texte';
import { SafeAreaView } from 'react-native-safe-area-context';
import { Ionicons, Trash2 } from '../components/icons';
import { Button } from '../components/Button';
import { ScreenHeader } from '../components/ScreenHeader';
import { VignetteCv } from '../components/VignetteCv';
import { CvListeLarge } from '../components/CvListeLarge';
import { useGabarit } from '../hooks/useGabarit';
import { t } from '../i18n';
import { listSavedCvs, setPrimaryCv, deleteCv, type SavedCv } from '../services/cv';
import { savedCvToGeneratedCv } from '../utils/savedCv';
import { formatSavedDate } from '../utils/cvDates';
import { colors, formes, gradients, radius, screenPadding, shadow, spacing, typography } from '../theme/tokens';
import type { CvCreateInitial } from './CvCreateScreen';
import { confirmer } from '../utils/confirmation';
import { citation, styleMiroir } from '../i18n/sens';

export interface CvListProps {
  onOpenCv: (initial: CvCreateInitial) => void;
  onCreate: () => void;
  onBack?: () => void;
}

type LoadState = 'loading' | 'error' | 'ready';

function toInitial(cv: SavedCv): CvCreateInitial {
  return {
    cvId: cv.id,
    cv: savedCvToGeneratedCv(cv),
    title: cv.title,
    template: cv.template,
    accentColor: cv.accent_color,
  };
}

// Ce que le lecteur d'écran dit d'un CV : titre, rang, date, langue et note. Le bouton se lit d'un
// trait, un libellé réduit au titre ferait disparaître « Principal » et la note.
function descriptionCv(cv: SavedCv): string {
  return [
    cv.title,
    cv.is_primary ? t('cvList.primary') : null,
    dateEtLangue(cv),
    cv.ats_score !== null ? t('cvList.score', { score: cv.ats_score }) : null,
  ]
    .filter(Boolean)
    .join(', ');
}

// « 18 oct. 2026 · FR » : la date suit la langue de l'interface, la langue est celle du CV.
function dateEtLangue(cv: SavedCv): string {
  return `${formatSavedDate(cv.created_at)} · ${(cv.output_language ?? 'fr').toUpperCase()}`;
}

export function CvListScreen({ onOpenCv, onCreate, onBack }: CvListProps) {
  const { large } = useGabarit();
  const [cvs, setCvs] = useState<SavedCv[]>([]);
  const [state, setState] = useState<LoadState>('loading');
  // Un seul CV a ses actions ouvertes à la fois (« Définir comme principal », « Supprimer »).
  const [ouvert, setOuvert] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const list = await listSavedCvs();
      setCvs(list);
      setState('ready');
    } catch {
      setState('error');
    }
  }, []);

  // À chaque arrivée sur l'écran, pas seulement au montage : revenue de l'éditeur, la liste doit
  // montrer le CV qu'on vient d'enregistrer. La relecture garde la liste affichée (l'état reste
  // « prêt ») : ni roue, ni « aucun CV » le temps qu'elle aboutisse.
  useFocusEffect(
    useCallback(() => {
      void load();
    }, [load]),
  );

  const makePrimary = async (id: string) => {
    setOuvert(null);
    try {
      await setPrimaryCv(id);
      await load();
    } catch {
      setState('error');
    }
  };

  const confirmDelete = (cv: SavedCv) => {
    confirmer(t('cvList.deleteTitle'), t('cvList.deleteBody', { title: citation(cv.title) }), [
      { text: t('common.cancel'), style: 'cancel' },
      {
        text: t('common.delete'),
        style: 'destructive',
        onPress: async () => {
          setOuvert(null);
          try {
            await deleteCv(cv.id);
            await load();
          } catch {
            setState('error');
          }
        },
      },
    ]);
  };

  // Le CV principal en tête ; sans principal déclaré, le plus récent ouvre la liste sans le prétendre.
  const principal = cvs.find((cv) => cv.is_primary) ?? cvs[0];
  const autres = cvs.filter((cv) => cv.id !== principal?.id);

  return (
    <SafeAreaView style={styles.container} edges={['top']}>
      <ScreenHeader
        onBack={onBack}
        variante="carte"
        title={t('cvList.title')}
        sousTitre={state === 'ready' ? t('myCv.savedCount', { count: cvs.length }) : undefined}
        testID="cvl-back"
        droite={
          <Pressable
            testID="cvl-create"
            accessibilityRole="button"
            accessibilityLabel={t('myCv.createShort')}
            onPress={onCreate}
            style={styles.plus}
          >
            <LinearGradient
              colors={[...gradients.cta]}
              start={{ x: 0, y: 0 }}
              end={{ x: 1, y: 0 }}
              style={StyleSheet.absoluteFill}
            />
            <Ionicons name="add" size={26} color={colors.white} />
          </Pressable>
        }
      />

      {state === 'loading' ? (
        <View style={styles.center}>
          <ActivityIndicator testID="cvl-loading" color={colors.bluePrimary} />
        </View>
      ) : null}

      {state === 'error' ? (
        <View style={styles.center}>
          <Text testID="cvl-error" style={styles.error}>
            {t('cvList.loadError')}
          </Text>
          <Button title={t('common.retry')} variant="secondary" onPress={() => void load()} testID="cvl-retry" />
        </View>
      ) : null}

      {state === 'ready' && cvs.length === 0 ? (
        <View testID="cvl-empty" style={styles.center}>
          <Text style={styles.emptyTitle}>{t('cvList.emptyTitle')}</Text>
          <Text style={styles.emptyText}>{t('cvList.emptyText')}</Text>
          <Button title={t('cvList.emptyCta')} withArrow onPress={onCreate} testID="cvl-empty-create" />
        </View>
      ) : null}

      {state === 'ready' && principal && large ? (
        <CvListeLarge
          cvs={[principal, ...autres]}
          principalId={principal.id}
          onOuvrir={(cv, edit) => onOpenCv(edit ? { ...toInitial(cv), edit: true } : toInitial(cv))}
          onPrincipal={(id) => void makePrimary(id)}
          onSupprimer={confirmDelete}
        />
      ) : null}
      {state === 'ready' && principal && !large ? (
        <ScrollView contentContainerStyle={styles.list} showsVerticalScrollIndicator={false}>
          <View style={styles.grandeCarte}>
            <View style={styles.grandeCarteHaut}>
              <Pressable
                testID={`cvl-card-${principal.id}`}
                accessibilityRole="button"
                accessibilityLabel={descriptionCv(principal)}
                onPress={() => onOpenCv(toInitial(principal))}
                style={styles.grandeCarteCorps}
              >
                <VignetteCv template={principal.template} accentColor={principal.accent_color} taille="lg" />
                <View style={styles.grandeCarteTextes}>
                  <View style={styles.badges}>
                    {principal.is_primary ? (
                      <View testID={`cvl-primary-${principal.id}`} style={styles.badgePrincipal}>
                        <Ionicons name="star" size={12} color={colors.electricInk} />
                        <Text style={styles.badgePrincipalTexte}>{t('cvList.primary')}</Text>
                      </View>
                    ) : null}
                    <Text testID={`cvl-lang-${principal.id}`} style={styles.date}>
                      {dateEtLangue(principal)}
                    </Text>
                  </View>
                  <Text style={styles.grandTitre} numberOfLines={3}>
                    {principal.title}
                  </Text>
                  {principal.ats_score !== null ? (
                    <Text testID={`cvl-score-${principal.id}`} style={styles.score}>
                      {t('cvList.score', { score: principal.ats_score })}
                    </Text>
                  ) : null}
                </View>
              </Pressable>
              <Pressable
                testID={`cvl-delete-${principal.id}`}
                accessibilityRole="button"
                accessibilityLabel={t('common.delete')}
                onPress={() => confirmDelete(principal)}
                hitSlop={8}
                style={styles.corbeille}
              >
                <Trash2 size={20} color={colors.grey500} strokeWidth={2} />
              </Pressable>
            </View>
            <View style={styles.boutons}>
              <Pressable
                testID={`cvl-edit-${principal.id}`}
                accessibilityRole="button"
                onPress={() => onOpenCv({ ...toInitial(principal), edit: true })}
                style={[styles.bouton, styles.boutonSombre]}
              >
                <Ionicons name="pencil" size={14} color={colors.white} />
                <Text style={[styles.boutonTexte, { color: colors.white }]}>{t('cvList.edit')}</Text>
              </Pressable>
              <Pressable
                testID={`cvl-preview-${principal.id}`}
                accessibilityRole="button"
                onPress={() => onOpenCv(toInitial(principal))}
                style={[styles.bouton, styles.boutonClair]}
              >
                <Ionicons name="eye-outline" size={15} color={colors.electricInk} />
                <Text style={[styles.boutonTexte, { color: colors.electricInk }]}>{t('cvList.preview')}</Text>
              </Pressable>
            </View>
            {/* Le principal peut être autre que le premier de la liste : sans principal déclaré,
                la grande carte reste un CV comme un autre et peut le devenir. */}
            {!principal.is_primary ? (
              <Pressable
                testID={`cvl-setprimary-${principal.id}`}
                accessibilityRole="button"
                onPress={() => void makePrimary(principal.id)}
                hitSlop={8}
                style={styles.lienPrincipal}
              >
                <Text style={styles.lienPrincipalTexte}>{t('cvList.setPrimary')}</Text>
              </Pressable>
            ) : null}
          </View>

          {autres.length > 0 ? (
            <>
              <View style={styles.entete}>
                <Text style={styles.sectionTitle}>{t('cvList.others')}</Text>
                <Text style={styles.compteur}>{autres.length}</Text>
              </View>
              <View style={styles.lignes}>
                {autres.map((cv, i) => (
                  <View key={cv.id} style={i < autres.length - 1 ? styles.separee : undefined}>
                    <View style={styles.ligne}>
                      <Pressable
                        testID={`cvl-card-${cv.id}`}
                        accessibilityRole="button"
                        accessibilityLabel={descriptionCv(cv)}
                        onPress={() => onOpenCv(toInitial(cv))}
                        style={styles.ligneCorps}
                      >
                        <VignetteCv template={cv.template} accentColor={cv.accent_color} taille="sm" />
                        <View style={styles.ligneTextes}>
                          <Text style={styles.ligneTitre} numberOfLines={1}>
                            {cv.title}
                          </Text>
                          <Text testID={`cvl-lang-${cv.id}`} style={styles.date}>
                            {dateEtLangue(cv)}
                          </Text>
                        </View>
                        {cv.ats_score !== null ? (
                          <View testID={`cvl-score-${cv.id}`} style={styles.pastilleScore}>
                            <Text style={styles.pastilleScoreTexte}>{cv.ats_score}</Text>
                          </View>
                        ) : null}
                      </Pressable>
                      <Pressable
                        testID={`cvl-more-${cv.id}`}
                        accessibilityRole="button"
                        accessibilityLabel={`${t('cvList.more')}, ${cv.title}`}
                        accessibilityState={{ expanded: ouvert === cv.id }}
                        onPress={() => setOuvert(ouvert === cv.id ? null : cv.id)}
                        hitSlop={4}
                        style={styles.plusLigne}
                      >
                        <View style={styleMiroir()}>
                          <Ionicons name="ellipsis-horizontal" size={20} color={colors.grey500} />
                        </View>
                      </Pressable>
                    </View>
                    {ouvert === cv.id ? (
                      <View testID={`cvl-actions-${cv.id}`} style={styles.actions}>
                        <Pressable
                          testID={`cvl-setprimary-${cv.id}`}
                          accessibilityRole="button"
                          onPress={() => void makePrimary(cv.id)}
                          hitSlop={8}
                          style={styles.action}
                        >
                          <Text style={styles.actionTexte}>{t('cvList.setPrimary')}</Text>
                        </Pressable>
                        <Pressable
                          testID={`cvl-delete-${cv.id}`}
                          accessibilityRole="button"
                          accessibilityLabel={t('common.delete')}
                          onPress={() => confirmDelete(cv)}
                          hitSlop={8}
                          style={styles.action}
                        >
                          <Trash2 size={18} color={colors.error} strokeWidth={2} />
                          <Text style={[styles.actionTexte, { color: colors.error }]}>{t('common.delete')}</Text>
                        </Pressable>
                      </View>
                    ) : null}
                  </View>
                ))}
              </View>
            </>
          ) : null}
        </ScrollView>
      ) : null}
    </SafeAreaView>
  );
}

const CARD_RADIUS = radius.xl;

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: colors.grey50 },
  plus: {
    width: formes.cible,
    height: formes.cible,
    borderRadius: radius.pill,
    overflow: 'hidden',
    alignItems: 'center',
    justifyContent: 'center',
  },
  center: { flex: 1, alignItems: 'center', justifyContent: 'center', padding: screenPadding, gap: spacing.md },
  list: { padding: screenPadding, gap: spacing.md, paddingBottom: spacing.xxl },

  // ── CV principal ──
  grandeCarte: {
    gap: spacing.md,
    padding: spacing.md,
    borderRadius: CARD_RADIUS,
    backgroundColor: colors.white,
    ...shadow.card,
  },
  grandeCarteHaut: { position: 'relative' },
  grandeCarteCorps: { flexDirection: 'row', gap: spacing.md },
  grandeCarteTextes: { flex: 1, minWidth: 0, gap: spacing.xs, paddingEnd: spacing.xl },
  badges: { flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap', gap: spacing.sm },
  badgePrincipal: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: spacing.sm,
    paddingVertical: 3,
    borderRadius: radius.pill,
    backgroundColor: colors.blueSoft,
  },
  badgePrincipalTexte: { fontSize: typography.caption.size, fontWeight: '800', color: colors.electricInk },
  date: { fontSize: typography.small.size, color: colors.grey500 },
  grandTitre: { fontSize: typography.h3.size, lineHeight: typography.h3.lineHeight, fontWeight: '800', color: colors.night },
  score: { fontSize: typography.small.size, fontWeight: '800', color: colors.night },
  corbeille: { position: 'absolute', top: 0, end: 0, padding: spacing.xs },
  boutons: { flexDirection: 'row', gap: spacing.sm },
  bouton: {
    flex: 1,
    minHeight: formes.cible,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: spacing.xs + 2,
    borderRadius: radius.pill,
  },
  boutonSombre: { backgroundColor: colors.night },
  boutonClair: { backgroundColor: colors.white, borderWidth: 1.5, borderColor: colors.bordureClaire },
  boutonTexte: { fontSize: typography.small.size, fontWeight: '800' },
  lienPrincipal: { alignSelf: 'flex-start', minHeight: formes.cible, justifyContent: 'center' },
  lienPrincipalTexte: { fontSize: typography.small.size, fontWeight: '800', color: colors.electricInk },

  // ── Autres CV ──
  entete: { flexDirection: 'row', alignItems: 'baseline', justifyContent: 'space-between', marginTop: spacing.sm },
  sectionTitle: {
    fontSize: typography.caption.size,
    fontWeight: '800',
    letterSpacing: typography.caption.letterSpacing,
    color: colors.grey500,
    textTransform: 'uppercase',
  },
  compteur: { fontSize: typography.small.size, fontWeight: '700', color: colors.grey500 },
  lignes: { borderRadius: CARD_RADIUS, backgroundColor: colors.white, overflow: 'hidden', ...shadow.card },
  separee: { borderBottomWidth: 1, borderBottomColor: colors.grey100 },
  ligne: { flexDirection: 'row', alignItems: 'center', paddingEnd: spacing.sm },
  ligneCorps: {
    flex: 1,
    minWidth: 0,
    minHeight: formes.hauteur.md,
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.md,
    paddingVertical: spacing.sm,
    paddingStart: spacing.lg,
  },
  ligneTextes: { flex: 1, minWidth: 0, gap: 2 },
  ligneTitre: { fontSize: typography.bodyMd.size, fontWeight: '800', color: colors.night },
  pastilleScore: {
    minWidth: 34,
    height: 24,
    paddingHorizontal: spacing.sm,
    borderRadius: 12,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.blueSoft,
  },
  pastilleScoreTexte: { fontSize: typography.small.size, fontWeight: '800', color: colors.electricInk },
  plusLigne: { width: formes.cible, height: formes.cible, alignItems: 'center', justifyContent: 'center' },
  actions: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: spacing.lg,
    paddingBottom: spacing.md,
  },
  action: { flexDirection: 'row', alignItems: 'center', gap: spacing.xs, minHeight: formes.cible },
  actionTexte: { fontSize: typography.small.size, fontWeight: '800', color: colors.electricInk },

  emptyTitle: { fontSize: typography.h2.size, fontWeight: typography.h2.weight, color: colors.grey900, textAlign: 'center' },
  emptyText: { fontSize: typography.body.size, color: colors.grey500, textAlign: 'center' },
  error: { fontSize: typography.body.size, color: colors.error, textAlign: 'center' },
});
